import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Loader2, ArrowLeft, KeyRound, Check, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function LoginPage({ onBack, onSuccess }) {
  const { t } = useTranslation();
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (mode === 'register') {
        await register(form.name, form.email, form.password);
      } else {
        await login(form.email, form.password);
      }
      onSuccess?.();
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (err.response?.status === 401) {
        setError(msg || 'Email o password non corretti');
      } else if (err.response) {
        setError(msg || `Errore server (${err.response.status}). Riprova.`);
      } else if (err.request) {
        setError('Errore di rete. Verifica la connessione e riprova.');
      } else {
        setError('Errore imprevisto. Riprova.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetCode, new_password: newPassword });
      setResetSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setForgotLoading(false);
    }
  };

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors";

  // Password Reset Success
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">{t('auth.passwordReset')}</h2>
            <p className="text-slate-400 text-sm mb-6">{t('auth.passwordResetSuccess')}</p>
            <button
              onClick={() => { setShowForgot(false); setResetSuccess(false); setForgotSent(false); setError(''); }}
              className="bg-teal-500 hover:bg-teal-400 text-black font-bold px-8 py-3 rounded-full transition-all"
              data-testid="back-to-login-button"
            >
              {t('auth.backToSignIn')}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Forgot Password Flow
  if (showForgot) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <button
            onClick={() => { setShowForgot(false); setForgotSent(false); setError(''); }}
            className="text-slate-400 hover:text-white mb-6 flex items-center space-x-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('auth.backToSignIn')}</span>
          </button>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-7 h-7 text-teal-400" />
              </div>
              <h1 className="text-2xl font-bold text-white font-outfit" data-testid="forgot-title">
                {forgotSent ? t('auth.checkEmail') : t('auth.resetPassword')}
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                {forgotSent ? `${t('auth.resetCodeSent')} ${forgotEmail}` : t('auth.enterEmail')}
              </p>
            </div>

            {!forgotSent ? (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                  <input
                    type="email"
                    placeholder={t('auth.email')}
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className={inputClass}
                    data-testid="forgot-email-input"
                  />
                </div>
                {error && <p className="text-rose-400 text-sm bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</p>}
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 text-black font-bold py-3 rounded-full transition-all flex items-center justify-center"
                  data-testid="send-reset-button"
                >
                  {forgotLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.sendResetCode')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetSubmit} className="space-y-4">
                <div className="relative">
                  <KeyRound className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder={t('auth.enterResetCode')}
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    maxLength={8}
                    className={`${inputClass} uppercase tracking-widest text-center font-mono`}
                    data-testid="reset-code-input"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    placeholder={t('auth.newPassword')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className={inputClass}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(p => !p)}
                    className="absolute right-3.5 top-3.5 text-slate-500 hover:text-slate-300 transition-colors"
                    data-testid="toggle-new-password-visibility"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {error && <p className="text-rose-400 text-sm bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</p>}
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 text-black font-bold py-3 rounded-full transition-all flex items-center justify-center"
                  data-testid="reset-password-button"
                >
                  {forgotLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.resetPassword')}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1419] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <button
          onClick={onBack}
          className="text-slate-400 hover:text-white mb-6 flex items-center space-x-2 transition-colors"
          data-testid="auth-back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </button>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="eSIM4You" className="w-16 h-16 mx-auto mb-4 rounded-xl" />
            <h1 className="text-2xl font-bold text-white font-outfit" data-testid="auth-title">
              {mode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {mode === 'login' ? t('auth.signInDesc') : t('auth.signUpDesc')}
            </p>
          </div>

          {/* Google Login */}
          <button
            onClick={loginWithGoogle}
            className="w-full bg-white/10 hover:bg-white/15 border border-white/10 text-white font-medium py-3 rounded-xl transition-all flex items-center justify-center space-x-3 mb-6"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{t('auth.continueGoogle')}</span>
          </button>

          <div className="flex items-center space-x-3 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-slate-500 text-xs">{t('common.or')}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div className="relative">
                <User className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder={t('auth.fullName')}
                  value={form.name}
                  onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  required
                  className={inputClass}
                  data-testid="input-name"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
              <input
                type="email"
                placeholder={t('auth.email')}
                value={form.email}
                onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                required
                className={inputClass}
                data-testid="input-email"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t('auth.password')}
                value={form.password}
                onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                required
                minLength={6}
                className={inputClass}
                data-testid="input-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                className="absolute right-3.5 top-3.5 text-slate-500 hover:text-slate-300 transition-colors"
                data-testid="toggle-password-visibility"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {error && (
              <p className="text-rose-400 text-sm bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2" data-testid="auth-error">
                {error}
              </p>
            )}

            {mode === 'login' && (
              <button
                type="button"
                onClick={() => { setShowForgot(true); setError(''); }}
                className="text-teal-400 hover:text-teal-300 text-sm transition-colors"
                data-testid="forgot-password-link"
              >
                {t('auth.forgotPassword')}
              </button>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 text-black font-bold py-3 rounded-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] flex items-center justify-center space-x-2"
              data-testid="auth-submit-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <span>{mode === 'login' ? t('auth.signIn') : t('auth.createAccount')}</span>
              )}
            </button>
          </form>

          <p className="text-center text-slate-400 text-sm mt-6">
            {mode === 'login' ? t('auth.noAccount') + ' ' : t('auth.haveAccount') + ' '}
            <button
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
              className="text-teal-400 hover:text-teal-300 font-medium"
              data-testid="auth-toggle-mode"
            >
              {mode === 'login' ? t('auth.signUp') : t('auth.signIn')}
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
}

export function AuthCallback({ onSuccess }) {
  const { handleGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (match) {
      handleGoogleCallback(match[1])
        .then(() => {
          window.history.replaceState({}, '', window.location.pathname);
          onSuccess?.();
        })
        .catch(() => {
          window.history.replaceState({}, '', '/');
          onSuccess?.();
        });
    }
  }, [handleGoogleCallback, onSuccess]);

  return (
    <div className="min-h-screen bg-[#0f1419] flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-teal-400 animate-spin" />
    </div>
  );
}
